import { memo, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useStore } from 'store/StoreContext';
import { saveGenerator as saveGeneratorSiteDisplayItems } from 'store/connect/SiteDisplayItemsData';
import { useUpdateRequiredProfile } from 'hooks/UseUpdateRequiredProfile';
import { copyObject } from 'common/copyObject';
import { User, initialUserData, callGetUserApi } from 'requests/RequestUser';
import { FormErrors, initialFormErrors, getErrorMessage } from 'validators/RequiredProfileValidator';
import { Country } from 'consts/CountryConst';
import { Prefecture } from 'consts/PrefectureConst';
import { useSearchAddress } from 'hooks/UseSearchAddress';
import { useSiteDisplayItems, SiteDisplayItemsExtend } from 'hooks/UseSiteDisplayItems';
import { SelectAddressDialog } from 'components/organisms/SelectAddressDialog';
import { RoutePath } from 'consts/RoutePath';
import { createBirthYearSelectValues } from 'common/birthYearCreator';
import { CompleteDialog } from 'components/organisms/CompleteDialog';
import { UpdateUserReqData } from 'requests/RequestUser';
import { CircularProgress, Backdrop } from '@material-ui/core';
import classes from './override.module.scss';
import { HttpStatus } from 'consts/HttpStatus';
import { convertHalfToFull, convertFullToHalf, convertFullKana } from 'common/CharacterConverter';
import { ReactError } from 'consts/ReactError';
import {
	GetLoginDisplayItemResData,
	callGetLoginDisplayItemApi,
	CommonLoginV2ResData,
	RequiredProfileEditRedirectResData,
	callCommonLoginV2AfterRequiredProfileEditApi,
} from 'requests/RequestLogin';
import {
	convertObjectToGetParameter,
	convertObjectToGetParameterWithEncode,
} from 'common/ObjectToGetParameterConverter';
import { DUPLICATION_LINK_URL_FOR_MAILADDRESS, GAKKEN_ID_FAQ_LINK_TEXT } from 'utils/constants';
import { Linkify } from 'components/molecules/linkify/Linkify';
import { MESSAGE_DUPLICATION_MAILADDRESS, MESSAGE_DUPLICATION_MAILADDRESS_SHORT } from 'utils/messages';

type Props = {};
export type UserForm = User & {
	birthYear: string;
	birthMonth: string;
	birthDate: string;
};

export const RequiredProfileEditTemplate = memo((_props: Props) => {
	const [store, dispatch] = useStore();
	const history = useHistory<UpdateUserReqData>();
	const { searchAddress, zipResponse, zipResponses, isDialogOpen, doSelect, doClose } = useSearchAddress();
	const [userFormData, setUserFormData] = useState<UserForm>({
		birthYear: '',
		birthMonth: '',
		birthDate: '',
		...initialUserData,
	});
	// 変更前と後で比較したいため、変更前の値を保持
	const [prevUserData, setPrevUserData] = useState<User>(initialUserData);
	const isMen = userFormData.sex === 0;
	const isWomen = userFormData.sex === 1;
	const [formErrors, setFormErrors] = useState<FormErrors>(initialFormErrors);
	const isFormError = !!Object.values(formErrors).find((e) => e !== '');
	const [actionError, setActionError] = useState('');
	const isActionError = actionError !== '';
	const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
	const [loginDisplayItem, setLoginDisplayItem] = useState<GetLoginDisplayItemResData>(undefined);
	const { displayItems, isRequired, isOptional, isNone, getSiteDisplayItemLoading } = useSiteDisplayItems(
		store,
		store.siteDisplayItemsExtendData.gid ? store.siteDisplayItemsExtendData : undefined,
	);
	useEffect(() => {
		const execute = async () => {
			if (displayItems) {
				await saveGeneratorSiteDisplayItems(dispatch, displayItems);
			}
		};
		execute();
	}, [displayItems]);
	const handleCompleteDialogOpen = () => {
		setIsCompleteDialogOpen(true);
	};
	const handleCompleteDialogClose = async () => {
		setIsCompleteDialogOpen(false);
		setProcessLoading(true);
		const authorizeV2Data = store.authorizeV2Data;
		// commonloginv2の続きからのAPIを叩く
		const result = await callCommonLoginV2AfterRequiredProfileEditApi(authorizeV2Data);
		if (result.status === HttpStatus.REDIRECT) {
			const redirectData = result.data as RequiredProfileEditRedirectResData;
			const getParameters = convertObjectToGetParameterWithEncode(
				authorizeV2Data,
				'code_challenge',
				'redirect_uri',
				'nonce',
			);
			if (redirectData.type === 'attribute') {
				history.push(`${RoutePath.ATTRIBUTE_AGREEMENT}?${getParameters}`);
			}
			return;
		}
		if (result.status !== HttpStatus.OK) {
			setActionError(ReactError.E9999);
			history.push(RoutePath.ERROR);
			return;
		}
		const commonLoginV2ResData = result.data as CommonLoginV2ResData;
		const getParameters = convertObjectToGetParameter(commonLoginV2ResData);
		window.location.href = `${
			authorizeV2Data.redirect_uri ? decodeURIComponent(authorizeV2Data.redirect_uri) : loginDisplayItem.redirect_url
		}?${getParameters}`;
		// setProcessLoading(false);
	};
	const [processLoading, setProcessLoading] = useState(false);
	const { update } = useUpdateRequiredProfile(
		formErrors,
		setFormErrors,
		getErrorMessage,
		actionError,
		setActionError,
		userFormData,
		prevUserData,
		handleCompleteDialogOpen,
		setProcessLoading,
		displayItems,
	);

	// ログイン情報を使ってユーザ情報を取得する
	const [getUserLoading, setGetUserLoading] = useState(false);
	useEffect(() => {
		const execute = async () => {
			setGetUserLoading(true);
			const authorizeV2Data = store.authorizeV2Data;
			const getDisplayItemResult = await callGetLoginDisplayItemApi({
				gid_dp: authorizeV2Data.gid_dp,
				site_cd: authorizeV2Data.client_id,
			});
			if (getDisplayItemResult.status !== HttpStatus.OK || !getDisplayItemResult.data) {
				setActionError(ReactError.E0008);
				history.push(RoutePath.ERROR);
				return;
			}
			setLoginDisplayItem(getDisplayItemResult.data);

			const result = await callGetUserApi();
			if (result.status !== HttpStatus.OK) {
				history.push(RoutePath.ERROR);
				setGetUserLoading(false);
				setActionError('ユーザ情報を取得できませんでした。');
				return;
			}
			setPrevUserData(result.data);
			const user = copyObject(result.data);
			// 編集中のデータがあれば上書きする
			if (!!history.location.state) {
				const editData = history.location.state.data;
				user.gid = editData.gid;
				user.mailaddress_1 = editData.mailaddress_1;
				user.lastname = editData.lastname;
				user.firstname = editData.firstname;
				user.kana_lastname = editData.kana_lastname;
				user.kana_firstname = editData.kana_firstname;
				user.birth = editData.birth;
				user.nickname = editData.nickname;
				user.sex = editData.sex;
				user.country = editData.country;
				user.zip = editData.zip;
				user.tel = editData.tel;
				user.pref_cd = editData.pref_cd;
				user.address_2 = editData.address_2;
				user.address_3 = editData.address_3;
				user.address_4 = editData.address_4;
				user.directmail_flg = editData.directmail_flg;
				user.data_util_agree_flg = editData.data_util_agree_flg;
			}
			const birth = user.birth ? new Date(user.birth) : undefined;
			setUserFormData({
				birthYear: !!birth ? birth.getFullYear().toString() : '',
				birthMonth: !!birth ? (birth.getMonth() + 1).toString() : '',
				birthDate: !!birth ? birth.getDate().toString() : '',
				...user,
				pref_cd: user.pref_cd.trim(),
				country: user.country.trim(),
			});
			// ユーザ情報取得したタイミングでもう一度プレースホルダ判定をする
			setIsPlaceHolders((prev) => ({
				...prev,
				pref_cd: user.pref_cd ? !user.pref_cd.trim() : true,
				birthYear: !birth,
				birthMonth: !birth,
				birthDate: !birth,
				country: user.country ? !user.country.trim() : true,
			}));
			setGetUserLoading(false);
		};
		execute();
	}, []);

	// 各入力欄のonChangeで呼ぶ
	const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setUserFormData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
	};
	const handleChangeRadioValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setUserFormData((prev) => ({ ...prev, [event.target.name]: Number(event.target.value) }));
	};
	const handleChangeCheckValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setUserFormData((prev) => ({ ...prev, [event.target.name]: event.target.checked ? 1 : 0 }));
	};
	const [isPlaceHolders, setIsPlaceHolders] = useState({
		pref_cd: !userFormData.pref_cd,
		birthYear: !userFormData.birthYear,
		birthMonth: !userFormData.birthMonth,
		birthDate: !userFormData.birthDate,
		country: !userFormData.country,
	});
	const handleChangeSelectValueWithPlaceHolder = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setIsPlaceHolders((prev) => ({ ...prev, [event.target.name]: !event.target.value }));
		setUserFormData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
	};
	const handleBlurConvertHalfToFullValue = (event: React.FocusEvent<HTMLInputElement>) => {
		setUserFormData((prev) => ({ ...prev, [event.target.name]: convertHalfToFull(event.target.value) }));
	};
	const handleBlurConvertFullToHalfValue = (event: React.FocusEvent<HTMLInputElement>) => {
		setUserFormData((prev) => ({ ...prev, [event.target.name]: convertFullToHalf(event.target.value) }));
	};
	const handleBlurConvertFullKanaValue = (event: React.FocusEvent<HTMLInputElement>) => {
		setUserFormData((prev) => ({ ...prev, [event.target.name]: convertFullKana(event.target.value) }));
	};

	// 住所検索関連
	const search = async () => {
		await searchAddress(userFormData.zip);
	};
	useEffect(() => {
		if (zipResponse) {
			// 入力欄に格納
			setUserFormData((prev) => ({
				...prev,
				zip: zipResponse.zipcode,
				pref_cd: zipResponse.prefcode,
				address_2: zipResponse.address2,
				address_3: zipResponse.address3,
			}));
			setIsPlaceHolders((prev) => ({ ...prev, pref_cd: !zipResponse.prefcode }));
		}
	}, [zipResponse]);

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenID不足属性入力';
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isFormError || isActionError) {
			errorRef.current.scrollIntoView();
		}
	}, [formErrors, actionError]);

	return (
		<main>
			<Backdrop className={classes.backdrop} open={getUserLoading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<section className="section mypage" ref={scrollRef}>
				<h1 className="page-head">不足属性入力</h1>
				<div className="section-in">
					<div className="section-txt-box" style={{ marginBottom: 40 }}>
						<div className="txt">
							<div>
								サービスサイト（{loginDisplayItem?.title}）
								で必要となる項目に、未入力があります。以下項目を入力してください
							</div>
						</div>
					</div>
					{isFormError || isActionError ? (
						<div className="error-box" ref={errorRef}>
							{isActionError ? <p>{actionError}</p> : null}
							{isFormError ? (
								<>
									<p>※下記項目は全て必須となります。</p>
									<div className="error-box-in">
										{formErrors.mailaddress_1 !== '' ? (
											<p>
												【メールアドレス】
												{formErrors.mailaddress_1 === MESSAGE_DUPLICATION_MAILADDRESS ? (
													<Linkify
														text={formErrors.mailaddress_1}
														targetWord={GAKKEN_ID_FAQ_LINK_TEXT}
														linkUrl={DUPLICATION_LINK_URL_FOR_MAILADDRESS}
													/>
												) : (
													formErrors.mailaddress_1
												)}
											</p>
										) : null}
										{formErrors.lastname !== '' || formErrors.firstname !== '' ? (
											<p>【お名前】{formErrors.lastname || formErrors.firstname}</p>
										) : null}
										{formErrors.kana_lastname !== '' || formErrors.kana_firstname !== '' ? (
											<p>【お名前（カナ）】{formErrors.kana_lastname || formErrors.kana_firstname}</p>
										) : null}
										{formErrors.birthYear !== '' || formErrors.birthMonth !== '' || formErrors.birthDate !== '' ? (
											<p>【生年月日】{formErrors.birthYear || formErrors.birthMonth || formErrors.birthDate}</p>
										) : null}
										{formErrors.sex !== '' ? <p>【性別】{formErrors.sex}</p> : null}
										{formErrors.tel !== '' ? <p>【電話番号】{formErrors.tel}</p> : null}
										{formErrors.zip !== '' ? <p>【郵便番号】{formErrors.zip}</p> : null}
										{formErrors.pref_cd !== '' ? <p>【都道府県】{formErrors.pref_cd}</p> : null}
										{formErrors.address_2 !== '' ? <p>【市区町村】{formErrors.address_2}</p> : null}
										{formErrors.address_3 !== '' ? <p>【町名・番地】{formErrors.address_3}</p> : null}
										{formErrors.address_4 !== '' ? <p>【マンション名】{formErrors.address_4}</p> : null}
										{formErrors.nickname !== '' ? <p>【ニックネーム】{formErrors.nickname}</p> : null}
										{formErrors.country !== '' ? <p>【国】{formErrors.country}</p> : null}
									</div>
								</>
							) : null}
						</div>
					) : null}
					<div className="form-box">
						<div className="form-head">
							<label>GakkenID</label>
						</div>
					</div>
					<div className="info-box">
						<div className="info-txt">{userFormData.gid || '　'}</div>
					</div>
					{isNone(displayItems.mailaddress_1) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="mailaddress_1">メールアドレス</label>
								{isRequired(displayItems.mailaddress_1) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.mailaddress_1) ? <span className="optional">（任意）</span> : null}
							</div>
							<div className="form-input width50">
								<input
									id="mailaddress_1"
									type="text"
									name="mailaddress_1"
									placeholder="sample@gakken.com"
									value={userFormData.mailaddress_1}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertFullToHalfValue}
								/>
							</div>
							<p className="error">
								{formErrors.mailaddress_1 === MESSAGE_DUPLICATION_MAILADDRESS
									? MESSAGE_DUPLICATION_MAILADDRESS_SHORT
									: formErrors.mailaddress_1}
							</p>
						</div>
					)}
					{isNone(displayItems.lastname) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="name">お名前（全角）</label>
								{isRequired(displayItems.lastname) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.lastname) ? <span className="optional">（任意）</span> : null}
								<span className="annotation">※全角</span>
							</div>
							<div className="form-input width50">
								<input
									id="name"
									type="text"
									name="lastname"
									placeholder="姓"
									value={userFormData.lastname}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertHalfToFullValue}
								/>
							</div>
							<p className="error">{formErrors.lastname}</p>
							<div className="form-input width50">
								<input
									type="text"
									name="firstname"
									placeholder="名"
									value={userFormData.firstname}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertHalfToFullValue}
								/>
							</div>
							<p className="error">{formErrors.firstname}</p>
						</div>
					)}
					{isNone(displayItems.kana_lastname) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="kana_name">お名前（カナ）</label>
								{isRequired(displayItems.kana_lastname) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.kana_lastname) ? <span className="optional">（任意）</span> : null}
								<span className="annotation">※全角カタカナ</span>
							</div>
							<div className="form-input width50">
								<input
									id="kana_name"
									type="text"
									name="kana_lastname"
									placeholder="セイ"
									value={userFormData.kana_lastname}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertFullKanaValue}
								/>
							</div>
							<p className="error">{formErrors.kana_lastname}</p>
							<div className="form-input width50">
								<input
									type="text"
									name="kana_firstname"
									placeholder="メイ"
									value={userFormData.kana_firstname}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertFullKanaValue}
								/>
							</div>
							<p className="error">{formErrors.kana_firstname}</p>
						</div>
					)}
					{isNone(displayItems.birth) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="birthYear">生年月日</label>
								{isRequired(displayItems.birth) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.birth) ? <span className="optional">（任意）</span> : null}
							</div>
							<div className="form-input width50">
								<div className="select-box">
									<select
										id="birthYear"
										name="birthYear"
										className={isPlaceHolders.birthYear ? classes.placeholderOption : ''}
										value={userFormData.birthYear}
										onChange={handleChangeSelectValueWithPlaceHolder}
									>
										<option value="" className={classes.placeholderOption}>
											年
										</option>
										{createBirthYearSelectValues().map((v) => {
											return (
												<option value={`${v}`} className={classes.defaultOption}>
													{v}
												</option>
											);
										})}
									</select>
								</div>
							</div>
							<p className="error">{formErrors.birthYear}</p>
							<div className="form-input width50">
								<div className="select-box">
									<label htmlFor="birthMonth" className="visually-hidden">
										月
									</label>
									<select
										id="birthMonth"
										name="birthMonth"
										className={isPlaceHolders.birthMonth ? classes.placeholderOption : ''}
										value={userFormData.birthMonth}
										onChange={handleChangeSelectValueWithPlaceHolder}
									>
										<option value="" className={classes.placeholderOption}>
											月
										</option>
										{new Array(12).fill(0).map((v, i) => {
											return (
												<option value={`${i + 1}`} className={classes.defaultOption}>
													{i + 1}
												</option>
											);
										})}
									</select>
								</div>
							</div>
							<p className="error">{formErrors.birthMonth}</p>
							<div className="form-input width50">
								<div className="select-box">
									<label htmlFor="birthDate" className="visually-hidden">
										日
									</label>
									<select
										id="birthDate"
										name="birthDate"
										className={isPlaceHolders.birthDate ? classes.placeholderOption : ''}
										value={userFormData.birthDate}
										onChange={handleChangeSelectValueWithPlaceHolder}
									>
										<option value="" className={classes.placeholderOption}>
											日
										</option>
										{new Array(31).fill(0).map((v, i) => {
											return (
												<option value={`${i + 1}`} className={classes.defaultOption}>
													{i + 1}
												</option>
											);
										})}
									</select>
								</div>
							</div>
							<p className="error">{formErrors.birthDate}</p>
						</div>
					)}
					{isNone(displayItems.sex) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label>性別</label>
								{isRequired(displayItems.sex) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.sex) ? <span className="optional">（任意）</span> : null}
							</div>
							<div className="form-input">
								<div className="radio-box">
									<ul>
										<li>
											<label className={`radio-label${isMen ? ' is-active' : ''}`} htmlFor="male">
												<input
													type="radio"
													id="male"
													name="sex"
													value={0}
													checked={isMen}
													onChange={handleChangeRadioValue}
												/>
												男
											</label>
										</li>
										<li>
											<label className={`radio-label${isWomen ? ' is-active' : ''}`} htmlFor="female">
												<input
													type="radio"
													id="female"
													name="sex"
													value={1}
													checked={isWomen}
													onChange={handleChangeRadioValue}
												/>
												女
											</label>
										</li>
									</ul>
								</div>
							</div>
							<p className="error">{formErrors.sex}</p>
						</div>
					)}
					{isNone(displayItems.tel) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="tel">電話番号</label>
								{isRequired(displayItems.tel) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.tel) ? <span className="optional">（任意）</span> : null}
								<span className="annotation">※半角数字</span>
							</div>
							<div className="form-input width50">
								<input
									id="tel"
									type="text"
									name="tel"
									placeholder="12345678910"
									value={userFormData.tel}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertFullToHalfValue}
								/>
							</div>
							<p className="error">{formErrors.tel}</p>
						</div>
					)}
					{isNone(displayItems.zip) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="zip">郵便番号</label>
								{isRequired(displayItems.zip) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.zip) ? <span className="optional">（任意）</span> : null}
								<span className="annotation">※半角数字 ハイフンなし</span>
							</div>
							<div className="form-input width50 form-input-address">
								<input
									id="zip"
									type="text"
									name="zip"
									placeholder="1234567"
									value={userFormData.zip}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertFullToHalfValue}
								/>
								<div className="address-search-btn">
									<button onClick={search}>住所検索</button>
								</div>
							</div>
							<p className="error">{formErrors.zip}</p>
						</div>
					)}
					{isNone(displayItems.pref_cd) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="pref_cd">都道府県</label>
								{isRequired(displayItems.pref_cd) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.pref_cd) ? <span className="optional">（任意）</span> : null}
							</div>
							<div className="form-input width50">
								<div className="select-box">
									<select
										id="pref_cd"
										name="pref_cd"
										className={isPlaceHolders.pref_cd ? classes.placeholderOption : ''}
										value={userFormData.pref_cd}
										onChange={handleChangeSelectValueWithPlaceHolder}
									>
										<option value="" className={classes.placeholderOption}>
											都道府県を選択
										</option>
										{Object.values(Prefecture).map((v) => {
											return (
												<option value={v.key} className={classes.defaultOption}>
													{v.name}
												</option>
											);
										})}
									</select>
								</div>
							</div>
							<p className="error">{formErrors.pref_cd}</p>
						</div>
					)}
					{isNone(displayItems.address_2) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="address_2">市区町村</label>
								{isRequired(displayItems.address_2) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.address_2) ? <span className="optional">（任意）</span> : null}
							</div>
							<div className="form-input width50">
								<input
									id="address_2"
									type="text"
									name="address_2"
									placeholder="品川区"
									value={userFormData.address_2}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertHalfToFullValue}
								/>
							</div>
							<p className="error">{formErrors.address_2}</p>
						</div>
					)}
					{isNone(displayItems.address_3) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="address_3">町名・番地</label>
								{isRequired(displayItems.address_3) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.address_3) ? <span className="optional">（任意）</span> : null}
								<span className="annotation">※全角</span>
							</div>
							<div className="form-input width50">
								<input
									id="address_3"
									type="text"
									name="address_3"
									placeholder="西五反田２−１１−８"
									value={userFormData.address_3}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertHalfToFullValue}
								/>
							</div>
							<p className="error">{formErrors.address_3}</p>
						</div>
					)}
					{isNone(displayItems.address_4) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="address_4">マンション名</label>
								{isRequired(displayItems.address_4) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.address_4) ? <span className="optional">（任意）</span> : null}
								<span className="annotation">※全角</span>
							</div>
							<div className="form-input width50">
								<input
									id="address_4"
									type="text"
									name="address_4"
									placeholder="○○マンション１０１号室"
									value={userFormData.address_4}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertHalfToFullValue}
								/>
							</div>
							<p className="error">{formErrors.address_4}</p>
						</div>
					)}
					{isNone(displayItems.nickname) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="nickname">ニックネーム</label>
								{isRequired(displayItems.nickname) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.nickname) ? <span className="optional">（任意）</span> : null}
								<span className="annotation">※全角</span>
							</div>
							<div className="form-input width50">
								<input
									id="nickname"
									type="text"
									name="nickname"
									placeholder="がっけんたろう"
									value={userFormData.nickname}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertHalfToFullValue}
								/>
							</div>
							<p className="error">{formErrors.nickname}</p>
						</div>
					)}
					{isNone(displayItems.country) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="country">国</label>
								{isRequired(displayItems.country) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.country) ? <span className="optional">（任意）</span> : null}
							</div>
							<div className="form-input width50">
								<div className="select-box">
									<select
										id="country"
										name="country"
										className={isPlaceHolders.country ? classes.placeholderOption : ''}
										value={userFormData.country}
										onChange={handleChangeSelectValueWithPlaceHolder}
									>
										<option value="" className={classes.placeholderOption}>
											国を選択
										</option>
										{Object.values(Country).map((v) => {
											return (
												<option value={v.key} className={classes.defaultOption}>
													{v.name}
												</option>
											);
										})}
									</select>
								</div>
							</div>
							<p className="error">{formErrors.country}</p>
						</div>
					)}
					<div className="form-box">
						<div className="form-input">
							<div className="check-box">
								<ul>
									{isNone(displayItems.directmail_flg) ? null : (
										<li>
											<label
												className={`check-label ${userFormData.directmail_flg === 1 ? 'is-active' : ''}`}
												htmlFor="receive"
											>
												<input
													type="checkbox"
													id="receive"
													name="directmail_flg"
													checked={userFormData.directmail_flg === 1}
													onChange={handleChangeCheckValue}
												/>
												GakkenIDのメルマガを受け取る
											</label>
										</li>
									)}
								</ul>
							</div>
						</div>
					</div>
					<div className="form-box">
						<div className="form-input">
							<div className="check-box">
								<ul>
									{isNone(displayItems.data_util_agree_flg) ? null : (
										<li>
											<label
												className={`check-label ${userFormData.data_util_agree_flg === 1 ? 'is-active' : ''}`}
												htmlFor="agree"
											>
												<input
													type="checkbox"
													id="agree"
													name="data_util_agree_flg"
													checked={userFormData.data_util_agree_flg === 1}
													onChange={handleChangeCheckValue}
												/>
												サービス向上のためデータ活用に同意する
											</label>
										</li>
									)}
								</ul>
							</div>
						</div>
					</div>
					<div className="btn-box">
						<div className="btn gr">
							{processLoading ? <CircularProgress /> : <button onClick={update}>不足属性を登録する</button>}
						</div>
					</div>
				</div>
			</section>
			{/* 住所選択ダイアログ */}
			<SelectAddressDialog isOpen={isDialogOpen} data={zipResponses} doSelect={doSelect} doClose={doClose} />
			{/* 完了ダイアログ */}
			<CompleteDialog
				isOpen={isCompleteDialogOpen}
				doTrans={handleCompleteDialogClose}
				buttonLabel={loginDisplayItem?.title}
			/>
		</main>
	);
});
